import { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  generatePath, Navigate, useNavigate, useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import { findingStoryHref } from './marketingOpsStory';
import { WatchTowerShell } from './TargetedMarketingChrome';
import {
  findingsForTopic,
  linkedCompaniesForTopicFinding,
  registerTopicNamedMiss,
  scheduleForTopic,
  setTopicPaused,
  setTopicStatus,
  topicById,
  topicLinkedCompanyCount,
} from './topicWatchAgents';
import useWatchTowerMock from './useWatchTowerMock';
import { formatAgentType, formatIsoDate } from './watchTowerSelectors';

import styles from './scss/TargetedMarketing.module.scss';

function WatchTowerTopic(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const topicId = Number(id) || 0;
  const { state, update } = useWatchTowerMock();
  const topic = topicById(state, topicId);
  const schedule = topic ? scheduleForTopic(state, topic.id) : null;
  const findings = useMemo(
    () => (topic ? findingsForTopic(state, topic.id) : []),
    [state, topic],
  );
  const latestFinding = findings[0] || null;
  const linked = latestFinding
    ? linkedCompaniesForTopicFinding(state, latestFinding.id)
    : [];

  if (!topic) {
    return <Navigate to={AppRoutes.watchTowerTopics} replace />;
  }

  const onPause = () => {
    update((prev) => setTopicPaused(prev, topic.id, !topic.paused));
    toast.success(topic.paused
      ? t('targetedMarketing.topicResumed')
      : t('targetedMarketing.topicPaused'));
  };

  const onArchive = () => {
    const nextStatus = topic.status === 'archived' ? 'active' : 'archived';
    update((prev) => setTopicStatus(prev, topic.id, nextStatus));
    toast.success(nextStatus === 'archived'
      ? t('targetedMarketing.topicArchived')
      : t('targetedMarketing.topicRestored'));
  };

  const onRunNow = () => {
    if (topic.paused || topic.status === 'archived') {
      toast.error(t('targetedMarketing.topicRunBlocked'));
      return;
    }
    toast.error(t('targetedMarketing.runNowNotLive'));
  };

  const onOpenRuns = () => {
    if (schedule) {
      navigate(`${AppRoutes.watchTowerRuns}?scheduleId=${schedule.id}&topicId=${topic.id}`);
      return;
    }
    navigate(`${AppRoutes.watchTowerRuns}?topicId=${topic.id}`);
  };

  const onRegisterMiss = (missUid: string) => {
    update((prev) => registerTopicNamedMiss(prev, topic.id, missUid));
    toast.success(t('targetedMarketing.namedMissImported'));
  };

  const matchLabel = topic.matchKinds
    .map((kind) => t(`targetedMarketing.matchKind_${kind}`))
    .join(', ');

  return (
    <WatchTowerShell
      primary="topics"
      runsSubView={null}
      actions={(
        <div className={styles.toolbarActions}>
          <Button
            title={t('targetedMarketing.runNow')}
            onClick={onRunNow}
          />
          <Button
            title={t('targetedMarketing.openRuns')}
            variant={BUTTON_VARIANT.secondary}
            onClick={onOpenRuns}
          />
          {topic.status !== 'archived' ? (
            <Button
              title={topic.paused ? t('targetedMarketing.resume') : t('targetedMarketing.pause')}
              variant={BUTTON_VARIANT.secondary}
              onClick={onPause}
            />
          ) : null}
          <Button
            title={topic.status === 'archived'
              ? t('targetedMarketing.unarchive')
              : t('targetedMarketing.archive')}
            variant={BUTTON_VARIANT.secondary}
            onClick={onArchive}
          />
        </div>
      )}
    >
      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{topic.title}</h2>
        <p className={styles.muted}>{topic.uid}</p>
        <dl className={styles.payloadList}>
          <div className={styles.payloadRow}>
            <dt>{t('targetedMarketing.agents')}</dt>
            <dd>{formatAgentType(topic.agentType)}</dd>
          </div>
          <div className={styles.payloadRow}>
            <dt>{t('targetedMarketing.template')}</dt>
            <dd>{topic.templateName}</dd>
          </div>
          <div className={styles.payloadRow}>
            <dt>{t('targetedMarketing.matchKinds')}</dt>
            <dd>{matchLabel || t('targetedMarketing.none')}</dd>
          </div>
          <div className={styles.payloadRow}>
            <dt>{t('targetedMarketing.linkedCompanies')}</dt>
            <dd>{topicLinkedCompanyCount(state, topic.id)}</dd>
          </div>
          <div className={styles.payloadRow}>
            <dt>{t('targetedMarketing.lastRun')}</dt>
            <dd>{formatIsoDate(topic.lastRunAt)}</dd>
          </div>
          <div className={styles.payloadRow}>
            <dt>{t('targetedMarketing.beginDate')}</dt>
            <dd>{formatIsoDate(schedule?.startDate || null)}</dd>
          </div>
          <div className={styles.payloadRow}>
            <dt>{t('targetedMarketing.endDate')}</dt>
            <dd>{schedule?.endDate ? formatIsoDate(schedule.endDate) : t('targetedMarketing.noEndDate')}</dd>
          </div>
        </dl>
      </Panel>

      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('targetedMarketing.trainingBrief')}</h2>
        <p className={styles.muted}>{t('targetedMarketing.topicBriefChatHint')}</p>
        <p className={styles.preWrap}>{topic.briefMarkdown || t('targetedMarketing.none')}</p>
      </Panel>

      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('targetedMarketing.linkedCompanies')}</h2>
        {linked.length === 0 ? (
          <EmptyList text={t('targetedMarketing.emptyAffected')} />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('targetedMarketing.name')}</th>
                  <th>{t('targetedMarketing.country')}</th>
                  <th>{t('targetedMarketing.industry')}</th>
                  <th>{t('targetedMarketing.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {linked.map((company) => (
                  <tr key={company.id}>
                    <td>{company.name}</td>
                    <td>{company.addressCountry || t('targetedMarketing.none')}</td>
                    <td>{company.naceSection || t('targetedMarketing.none')}</td>
                    <td>
                      <Button
                        className={styles.rowAction}
                        title={t('targetedMarketing.openDossier')}
                        variant={BUTTON_VARIANT.secondary}
                        onClick={() => navigate(generatePath(AppRoutes.watchTowerCompany, {
                          id: String(company.id),
                        }))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('targetedMarketing.namedMisses')}</h2>
        {topic.namedMisses.length === 0 ? (
          <EmptyList text={t('targetedMarketing.none')} />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('targetedMarketing.name')}</th>
                  <th>{t('targetedMarketing.country')}</th>
                  <th>{t('targetedMarketing.uid')}</th>
                  <th>{t('targetedMarketing.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {topic.namedMisses.map((miss) => (
                  <tr key={miss.uid}>
                    <td>{miss.name}</td>
                    <td>{miss.addressCountry}</td>
                    <td>{miss.uid}</td>
                    <td>
                      {miss.importedWatchCompanyId ? (
                        <Button
                          className={styles.rowAction}
                          title={t('targetedMarketing.openDossier')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => navigate(generatePath(AppRoutes.watchTowerCompany, {
                            id: String(miss.importedWatchCompanyId),
                          }))}
                        />
                      ) : (
                        <Button
                          className={styles.rowAction}
                          title={t('targetedMarketing.registerNamedMiss')}
                          variant={BUTTON_VARIANT.secondary}
                          disabled={!latestFinding}
                          onClick={() => onRegisterMiss(miss.uid)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('targetedMarketing.levelFinding')}</h2>
        {findings.length === 0 ? (
          <EmptyList text={t('targetedMarketing.emptyFindings')} />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('targetedMarketing.severity')}</th>
                  <th>{t('targetedMarketing.title')}</th>
                  <th>{t('targetedMarketing.observedAt')}</th>
                  <th>{t('targetedMarketing.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {findings.map((finding) => (
                  <tr key={finding.publicId}>
                    <td>{finding.severity}</td>
                    <td>{finding.title}</td>
                    <td>{formatIsoDate(finding.observedAt)}</td>
                    <td>
                      <Button
                        className={styles.rowAction}
                        title={t('targetedMarketing.openMarketingOps')}
                        variant={BUTTON_VARIANT.secondary}
                        onClick={() => navigate(findingStoryHref(finding.publicId))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </WatchTowerShell>
  );
}

export default WatchTowerTopic;
